/**
 * `ErrorModalHost` — mounted ONCE at the app root, renders whatever
 * `showApiError()` / `showOfflineError()` raised.
 *
 * An API failure is a closable modal with a code and a
 * support path, never a toast that vanishes in three seconds while a fan is
 * still reading it. This host outranks every other overlay — it uses
 * `@sportsmet/ui`'s `Dialog` (a bottom sheet under 768px, a centered dialog
 * above it) and no feature overlay is allowed its own z-index that could tie
 * with or beat it.
 *
 * NOTE: `ErrorModalHost` is meant to become a `@sportsmet/ui`
 * export. The kit does not export one yet, so it lives here for now; when the
 * kit adds it, this file becomes a re-export and the tests below move with it.
 */
import { Button, Dialog } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  dismissApiError,
  subscribeToErrorModal,
  type typeErrorModalEvent,
} from "./apiErrorBus";

export const SUPPORT_EMAIL = "support@sportsmet.com";

export const ErrorModalHost = (): ReactNode => {
  const [event, setEvent] = useState<typeErrorModalEvent | null>(null);

  useEffect(() => subscribeToErrorModal(setEvent), []);

  const close = (): void => {
    dismissApiError();
    setEvent(null);
  };

  return (
    <Dialog
      open={event !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title="That didn’t work"
      description={event?.message ?? ""}
      // Nothing to type into, so focusing the content itself is correct — an
      // autofocused button here would put a fan one stray Enter away from
      // dismissing a message they have not read.
      autoFocus={false}
      footer={
        <Button variant="primary" fullWidth onClick={close}>
          Got it
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <p className="text-body text-ink-secondary">
          If this keeps happening, email {SUPPORT_EMAIL} and include the code
          below.
        </p>
        {event?.code === null || event === null ? null : (
          <p className="text-caption text-ink-tertiary">
            Error code:{" "}
            <span className="font-mono text-ink" data-testid="error-code">
              {event.code}
            </span>
          </p>
        )}
      </div>
    </Dialog>
  );
};
